import { useCallback, useRef, useState } from 'react';

const MAX_HISTORY = 30;

const drawDataUrlToCanvas = (sigPad, dataUrl) => {
  if (!sigPad || !dataUrl) return;

  const canvas = sigPad.getCanvas();
  const ctx = canvas.getContext('2d');
  const image = new Image();

  image.onload = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const fit =
      image.width && image.height
        ? Math.min(canvas.width / image.width, canvas.height / image.height, 1)
        : 1;
    const drawWidth = image.width * fit;
    const drawHeight = image.height * fit;

    ctx.drawImage(
      image,
      (canvas.width - drawWidth) / 2,
      (canvas.height - drawHeight) / 2,
      drawWidth,
      drawHeight
    );

    const pad = sigPad.getSignaturePad?.();
    if (pad) pad._isEmpty = false;
  };

  image.src = dataUrl;
};

export const useDrawHistory = (sigPadRef) => {
  const baselineRef = useRef(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const restore = useCallback(
    (dataUrl) => {
      if (!sigPadRef.current) return;
      sigPadRef.current.clear();
      if (!dataUrl) return;
      drawDataUrlToCanvas(sigPadRef.current, dataUrl);
    },
    [sigPadRef]
  );

  const setBaseline = useCallback((dataUrl) => {
    baselineRef.current = dataUrl || null;
    setUndoStack([]);
    setRedoStack([]);
  }, []);

  const capture = useCallback(() => {
    if (!sigPadRef.current) return;
    const snap = sigPadRef.current.isEmpty()
      ? null
      : sigPadRef.current.getCanvas().toDataURL('image/png');
    setUndoStack((prev) => {
      const next = [...prev, snap];
      if (next.length > MAX_HISTORY) next.shift();
      return next;
    });
    setRedoStack([]);
  }, [sigPadRef]);

  const undo = useCallback(() => {
    if (undoStack.length === 0) return baselineRef.current;
    const popped = undoStack[undoStack.length - 1];
    const nextUndo = undoStack.slice(0, -1);
    const target = nextUndo[nextUndo.length - 1] || baselineRef.current || null;
    setUndoStack(nextUndo);
    setRedoStack((prev) => [...prev, popped]);
    restore(target);
    return target;
  }, [undoStack, restore]);

  const redo = useCallback(() => {
    if (redoStack.length === 0) return null;
    const popped = redoStack[redoStack.length - 1];
    const nextRedo = redoStack.slice(0, -1);
    setRedoStack(nextRedo);
    setUndoStack((prev) => [...prev, popped]);
    restore(popped);
    return popped;
  }, [redoStack, restore]);

  const reset = useCallback(() => {
    baselineRef.current = null;
    setUndoStack([]);
    setRedoStack([]);
  }, []);

  return {
    setBaseline,
    capture,
    undo,
    redo,
    reset,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0
  };
};
